import React, { MouseEvent, useState } from 'react';

import {
  Box,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';

import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import SearchBar from 'features/common/SearchBar/SearchBar';
import { StlButton } from 'features/common/StlButton/StlButton';
import { GenericDatePicker } from 'features/task_bar/DatePicker';

import { Filters } from './types';

import styles from './NotificationLog.module.css';

const buttonSx = {
  position: 'absolute',
  top: '0.5rem',
  right: '12px',
};

interface ISearchNav {
  filters: Filters;
  onChange: (patch: Partial<Filters>) => void;
}

export default function SearchNav({ filters, onChange }: ISearchNav) {
  const [openDay, setOpenDay] = useState<boolean>(false);

  const { searchTerm, resolved, date } = filters;
  const resetFilters = () => {
    onChange({
      searchTerm: undefined,
      resolved: undefined,
      date: undefined,
      routeId: undefined,
    });
  };

  const setResolved = (val) => {
    onChange({ resolved: val });
  };

  const setSearchTerm = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = e.target;
    onChange({ searchTerm: value || undefined });
  };

  const resetDate = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onChange({ date: undefined });
  };

  return (
    <Box className={styles.searchNav}>
      <StlButton
        handleClick={resetFilters}
        value="Reset Filters"
        width={85}
        variant="text"
        sx={buttonSx}
      />
      <SearchBar
        stable
        placeholder="Search by route name, alert or speed"
        searchTerm={searchTerm ?? ''}
        onChange={setSearchTerm}
        iconPos="left"
        iconCol="#8a8a8a"
        classes={[styles.searchField]}
      />
      <Box className={styles.datePickerWrapper}>
        <GenericDatePicker
          buttonClass={styles.lightBorder}
          allowNoDate
          open={openDay}
          setOpen={setOpenDay}
          targetDate={date}
          setNewTargetDate={(value) =>
            onChange({
              date: value,
            })
          }
          closeOnSelect
          fullWidth
          placeholderText="Active on"
        />
        {date && (
          <IconButton
            disableRipple
            size="small"
            title="Reset"
            className={styles.resetIcon}
            onClick={resetDate}
          >
            <FontAwesomeIcon name="xmark" type="sharp-solid" size="0.75rem" />
          </IconButton>
        )}
      </Box>
      <StatusMenu status={resolved} setStatus={setResolved} />
    </Box>
  );
}

interface IStatusMenu {
  status?: boolean;
  setStatus: (value: boolean | undefined) => void;
}

function StatusMenu({ setStatus, status }: IStatusMenu) {
  const handleChange = (e: SelectChangeEvent<number | ''>) => {
    const {value} = e.target;
    if (value === '') {
      setStatus(undefined);
    } else {
      setStatus(value === 1);
    }
  };

  return (
    <Select
      displayEmpty
      renderValue={(selected: string | number) => {
        if (selected === '')
          return <span className={styles.placeholder}>Any Status</span>;

        return selected === 1 ? 'Resolved' : 'Unresolved';
      }}
      className={styles.selectMenu}
      MenuProps={{
        PaperProps: {
          className: styles.selectMenuPaper,
        },
      }}
      value={status === undefined ? '' : Number(status)}
      onChange={handleChange}
      labelId="bin-size-picker-label"
      size="small"
    >
      <MenuItem value="">Any Status</MenuItem>
      <MenuItem value={1}>Resolved</MenuItem>
      <MenuItem value={0}>Unresolved</MenuItem>
    </Select>
  );
}
